import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Users, Wallet, ShoppingBag, ArrowDownToLine, Copy, Check, Send, Palette, UserPlus, Store, ArrowRight, CheckCircle2, AlertTriangle, PlugZap, CalendarClock } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { auditActionKey } from '../../lib/auditActions';
import { PageHeader, StatCard, Card, CardHeader, CardBody, TrendChart, LoadingBlock, Avatar } from '../../ui';
import { useCompanyStats, posHealthFromStats } from '../owner/company/OverviewTab';
import { ServioStatusCard } from '../../features/servio/ServioStatus';

const growth = (curr, prev) => (prev > 0 ? Math.round(((curr - prev) / prev) * 100) : null);

/** Главная кабинета компании */
export default function HomePage() {
  const { t, fmt } = useI18n();
  const { admin, company, canEdit } = useSession();
  const [stats] = useCompanyStats(company.id);
  const [billing, setBilling] = useState(null);
  useEffect(() => {
    api.get(`/admin/tenants/${company.id}`).then((res) => setBilling(res.data.data)).catch(() => setBilling(null));
  }, [company.id]);
  const dueDays = billing?.billing_next_date && billing.plan_price > 0
    ? Math.round((new Date(String(billing.billing_next_date).slice(0, 10)) - new Date(new Date().toISOString().slice(0, 10))) / 864e5)
    : null;
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    api.get('/admin/audit', { params: { limit: 6 } })
      .then((res) => setRecent(res.data.items || res.data.data || []))
      .catch(() => setRecent([]));
  }, []);

  if (!stats) return <LoadingBlock />;
  const spendGrowth = growth(stats.spend30.sum, stats.spend30.prev);

  const quick = [
    { to: '/company/notifications', label: t('home.quickPush'), icon: Send },
    { to: '/company/branding', label: t('home.quickBranding'), icon: Palette },
    { to: '/company/venues', label: t('home.quickVenues'), icon: Store },
    { to: '/company/team', label: t('home.quickTeam'), icon: UserPlus },
  ];

  return (
    <>
      <PageHeader title={t('home.greeting', { name: admin?.username || '' })} description={t('home.subtitle')} />

      {dueDays !== null && dueDays <= 7 && (
        <div className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${dueDays < 0
          ? 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200'
          : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200'}`} role="status">
          <CalendarClock className="mt-0.5 h-5 w-5 shrink-0" />
          <div>
            <p className="font-semibold">
              {dueDays < 0
                ? t('home.paymentOverdue', { days: -dueDays })
                : dueDays === 0 ? t('home.paymentToday') : t('home.paymentSoon', { days: dueDays })}
            </p>
            <p className="mt-0.5">{t('home.paymentDetails', { date: fmt.date(billing.billing_next_date), amount: fmt.money(billing.plan_price, billing.currency || 'UAH') })}</p>
          </div>
        </div>
      )}

      <PosBanner stats={stats} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label={t('company.kpi.employees')}
          value={fmt.number(stats.employees.total)}
          hint={t('company.kpi.employeesHint', { registered: fmt.number(stats.employees.registered), active7: fmt.number(stats.employees.active7) })}
          icon={Users}
        />
        <StatCard label={t('company.kpi.balance')} value={fmt.money(stats.balance)} hint={t('company.kpi.balanceHint', { cards: fmt.number(stats.cards) })} icon={Wallet} />
        <StatCard
          label={t('company.kpi.spend')}
          value={fmt.money(stats.spend30.sum)}
          delta={spendGrowth}
          hint={spendGrowth !== null ? t('overview.spendHint') : null}
          icon={ShoppingBag}
          tone="brand"
        />
        <StatCard label={t('company.kpi.topup')} value={fmt.money(stats.topup30)} icon={ArrowDownToLine} />
      </div>

      {stats.pos.configured && <div className="mt-6"><ServioStatusCard sync={stats.pos.sync} /></div>}

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card className="xl:col-span-2">
          <CardHeader
            title={t('overview.chartTitle')}
            description={t('company.last30')}
            actions={<Link to="/company/analytics" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('nav.analytics')}</Link>}
          />
          <CardBody>
            <TrendChart
              data={stats.trend}
              series={[{ key: 'spend', label: t('analytics.spend') }, { key: 'topup', label: t('analytics.topup') }]}
              formatValue={(v) => fmt.number(v, { notation: 'compact', maximumFractionDigits: 1 })}
              formatDate={(d) => fmt.date(d)}
            />
          </CardBody>
        </Card>

        <CompanyCodeCard code={company.company_code} />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-3">
        <Card>
          <CardHeader title={t('company.topVenues')} />
          {stats.top_venues.length ? (
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {stats.top_venues.map((v) => (
                <li key={v.name} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-zinc-900 dark:text-zinc-100">{v.name}</span>
                    <span className="text-xs text-zinc-500">{t('company.visits', { count: v.visits })}</span>
                  </span>
                  <span className="tabular shrink-0 text-zinc-900 dark:text-zinc-100">{fmt.money(v.sum)}</span>
                </li>
              ))}
            </ul>
          ) : <CardBody><p className="text-sm text-zinc-500">{t('company.noVenues')}</p></CardBody>}
        </Card>

        {canEdit && (
          <Card>
            <CardHeader title={t('home.quick')} />
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {quick.map(({ to, label, icon: Icon }) => (
                <li key={to}>
                  <Link to={to} className="flex items-center gap-3 px-5 py-3 text-sm text-zinc-700 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800/50">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-950/60 dark:text-brand-300"><Icon className="h-4 w-4" /></span>
                    <span className="flex-1">{label}</span>
                    <ArrowRight className="h-4 w-4 text-zinc-400" />
                  </Link>
                </li>
              ))}
            </ul>
          </Card>
        )}

        <Card className={canEdit ? '' : 'xl:col-span-2'}>
          <CardHeader
            title={t('home.recent')}
            actions={<Link to="/company/audit" className="text-xs font-medium text-brand-600 hover:underline dark:text-brand-400">{t('overview.allActivity')}</Link>}
          />
          {!recent ? <LoadingBlock /> : recent.length ? (
            <ul className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {recent.map((a) => {
                const key = auditActionKey(a.method, a.path);
                return (
                  <li key={a.id} className="flex items-center gap-3 px-5 py-3">
                    <Avatar name={a.admin_username} size="sm" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-zinc-800 dark:text-zinc-200">
                        <span className="font-medium">{a.admin_username}</span>
                        <span className="text-zinc-500"> · {key ? t(key) : t(`audit.methods.${a.method}`)}</span>
                      </p>
                      <p className="text-[11px] text-zinc-400">{fmt.relative(a.created_at)}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : <CardBody><p className="text-sm text-zinc-500">{t('home.noRecent')}</p></CardBody>}
        </Card>
      </div>
    </>
  );
}

function PosBanner({ stats }) {
  const { t, fmt } = useI18n();
  const health = posHealthFromStats(stats.pos);
  const sync = stats.pos.sync;
  if (health === 'unknown') return null;
  const tone = {
    ok: { icon: CheckCircle2, text: t('home.posOk'), cls: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300' },
    error: { icon: AlertTriangle, text: t('home.posProblem'), cls: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300' },
    degraded: { icon: AlertTriangle, text: t('home.posHistoryDown'), cls: 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300' },
    unconfigured: { icon: PlugZap, text: t('home.posNotConfigured'), cls: 'border-zinc-200 bg-zinc-50 text-zinc-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300' },
  }[health];
  const Icon = tone.icon;
  return (
    <div className={clsx('mb-6 flex flex-wrap items-center justify-between gap-2 rounded-xl border px-4 py-3 text-sm', tone.cls)} role="status">
      <span className="flex items-center gap-2"><Icon className="h-4 w-4 shrink-0" />{tone.text}</span>
      {sync?.last_success_at && <span className="text-xs opacity-80">{t('company.lastSync')}: {fmt.relative(sync.last_success_at)}</span>}
    </div>
  );
}

export function CompanyCodeCard({ code }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(t('common.error'));
    }
  };
  return (
    <Card>
      <CardHeader title={t('home.companyCode')} description={t('home.companyCodeHint')} />
      <CardBody className="space-y-5">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-brand-300 bg-brand-50/60 px-4 py-3 dark:border-brand-800 dark:bg-brand-950/30">
          <span className="font-mono text-2xl font-semibold tracking-[0.15em] text-brand-800 dark:text-brand-200">{code || '—'}</span>
          {code && (
            <button
              type="button"
              onClick={copy}
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 dark:text-brand-300 dark:hover:bg-brand-900/50"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? t('home.copied') : t('home.copy')}
            </button>
          )}
        </div>
        <div>
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-zinc-500">{t('home.howToJoin')}</p>
          <ol className="space-y-2">
            {[1, 2, 3].map((n) => (
              <li key={n} className="flex gap-3 text-sm text-zinc-700 dark:text-zinc-300">
                <span className="tabular flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-[11px] font-semibold text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400">{n}</span>
                {t(`home.steps.${n}`)}
              </li>
            ))}
          </ol>
        </div>
      </CardBody>
    </Card>
  );
}
